import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Edit, Plus } from "lucide-react";
import ChatProviderForm from "@/components/forms/chat/ChatProviderForm";
import {
    useCreateChatProviderMutation,
    useUpdateChatProviderMutation,
} from "@/api/mutations/admin/ChatProviderMutations";

interface ChatProviderDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    provider?: any; // Optional for create mode
    mode?: "create" | "edit";
}

export function ChatProviderDialog({ open, onOpenChange, provider, mode = "edit" }: ChatProviderDialogProps) {
    const [error, setError] = useState("");

    const createMutation = useCreateChatProviderMutation(undefined, setError);
    const updateMutation = useUpdateChatProviderMutation(undefined, setError);

    const isCreateMode = mode === "create";

    const handleSubmit = (data: any) => {
        if (isCreateMode) {
            // Create new provider
            const providerType = data.CHAT_PROVIDER || "azure";
            const displayName =
                data.display_name || `${providerType.charAt(0).toUpperCase() + providerType.slice(1)} Model`;
            createMutation.mutate({
                provider: providerType,
                display_name: displayName,
                config: data,
            });
        } else {
            // Update existing provider
            if (!provider) return;

            const newProviderType = data.CHAT_PROVIDER || provider.provider;
            const newDisplayName = data.display_name || provider.display_name;
            updateMutation.mutate({
                id: provider.id,
                data: {
                    provider: newProviderType,
                    display_name: newDisplayName,
                    config: data,
                },
            });
        }
    };

    // Close dialog on successful create/update
    useEffect(() => {
        if (createMutation.isSuccess || updateMutation.isSuccess) {
            onOpenChange(false);
            // Reset the mutation states when dialog closes
            createMutation.reset();
            updateMutation.reset();
        }
    }, [createMutation.isSuccess, updateMutation.isSuccess, onOpenChange, createMutation, updateMutation]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
                <DialogHeader>
                    <div className="flex items-center justify-between">
                        <DialogTitle className="flex items-center gap-2">
                            {isCreateMode ? (
                                <>
                                    <Plus className="h-5 w-5" />
                                    {provider ? `Duplicate ${provider.display_name} Model` : "Create New Model"}
                                </>
                            ) : (
                                <>
                                    <Edit className="h-5 w-5" />
                                    Edit {provider?.display_name || ""} Model
                                </>
                            )}
                        </DialogTitle>
                        {!isCreateMode && provider && (
                            <Badge variant={provider.enabled ? "default" : "secondary"}>
                                {provider.enabled ? "Enabled" : "Disabled"}
                            </Badge>
                        )}
                    </div>
                </DialogHeader>

                <div className="space-y-4">
                    {error && <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-600">{error}</div>}
                    <ChatProviderForm
                        initialData={
                            provider ? {
                                ...provider.config,
                                display_name: provider.display_name,
                                CHAT_PROVIDER: provider.provider || provider.config?.CHAT_PROVIDER,
                            } : {}
                        }
                        onSubmit={handleSubmit}
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
}
